import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  Input
} from '@repo/ui'
import { useForm } from '@tanstack/react-form'
import { Link, createFileRoute, redirect, useRouter } from '@tanstack/react-router'
import * as Sentry from '@sentry/react'
import { useEffect, useState } from 'react'
import { z } from 'zod'
import { apiUserOrgProfile } from '@/api/user/profile'
import { apiMfa, apiMfaResend } from '@/api/user/mfa'
import { apiLogin } from '@/api/user/login'
import { zodFieldValidator } from '@/common/parseDataType'

// UX-level validators for the login form. The generated LoginRequest schema is
// shape-only, so these value constraints live here on the form.
const loginFormSchema = z.object({
  email: z.email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required')
})

const fallback = '/' as const

export const Route = createFileRoute('/login')({
  validateSearch: z.object({
    redirect: z.string().optional().catch('')
  }),
  beforeLoad: ({ context, search }) => {
    if (context.user.isAuthenticated) {
      throw redirect({ to: search.redirect || fallback })
    }
  },
  head: () => ({ meta: [{ title: 'Login - Snowvault' }] }),
  component: Login
})

function Login() {
  const router = useRouter()
  const search = Route.useSearch()

  const [error, setError] = useState<string | undefined>(undefined)
  const [email, setEmail] = useState<string | undefined>(undefined)
  const [mfaRequired, setMfaRequired] = useState(false)
  const [resendCooldown, setResendCooldown] = useState(0)

  useEffect(() => {
    if (resendCooldown <= 0) return
    const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000)
    return () => clearTimeout(timer)
  }, [resendCooldown])

  async function handleMfaResend() {
    if (!email) return
    try {
      await apiMfaResend(email)
      setResendCooldown(60)
    } catch (err) {
      Sentry.captureException(err)
      console.error('MFA resend error:', err)
      setError('Failed to resend code. Please try again.')
    }
  }

  const loginForm = useForm({
    defaultValues: {
      email: '',
      password: ''
    },
    onSubmit: async ({ value }) => {
      setError(undefined)
      try {
        const loginRes = await apiLogin(value)
        if (loginRes.mfa_required) {
          setEmail(value.email)
          setMfaRequired(true)
        } else {
          const userOrgProfile = await apiUserOrgProfile()
          router.update({
            context: {
              ...router.options.context,
              user: {
                isAuthenticated: true,
                userOrgProfile
              }
            }
          })
          router.navigate({ to: search.redirect || fallback })
        }
      } catch (err) {
        Sentry.captureException(err)
        console.error('Login error:', err)
        setError('Login failed. Please check your credentials and try again.')
      }
    }
  })

  const mfaForm = useForm({
    defaultValues: {
      mfaCode: ''
    },
    onSubmit: async ({ value }) => {
      setError(undefined)
      try {
        if (!email) {
          throw new Error('Email is not set for MFA')
        }
        const userOrgProfile = await apiMfa({ email, code: value.mfaCode })
        router.update({
          context: {
            ...router.options.context,
            user: {
              isAuthenticated: true,
              userOrgProfile
            }
          }
        })
        router.navigate({ to: search.redirect || fallback })
      } catch (err) {
        Sentry.captureException(err)
        console.error('MFA error:', err)
        setError('MFA verification failed. Please check your code and try again.')
      }
    }
  })

  return (
    <div className="flex min-h-svh w-full items-center justify-center p-6 md:p-10">
      <div className="w-full max-w-sm">
        <div className="flex flex-col gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Login to your account</CardTitle>
              <CardDescription>
                {mfaRequired
                  ? 'Enter the verification code sent to your email'
                  : 'Enter your credentials to access your account'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!mfaRequired ? (
                <form
                  onSubmit={(e) => {
                    e.preventDefault()
                    loginForm.handleSubmit()
                  }}
                >
                  <FieldGroup>
                    <loginForm.Field
                      name="email"
                      validators={{
                        onBlur: zodFieldValidator(loginFormSchema.shape.email),
                        onSubmit: zodFieldValidator(loginFormSchema.shape.email)
                      }}
                    >
                      {(field) => (
                        <Field>
                          <FieldLabel htmlFor={field.name}>Email</FieldLabel>
                          <Input
                            id={field.name}
                            name={field.name}
                            type="email"
                            placeholder="Enter your email"
                            value={field.state.value}
                            onBlur={field.handleBlur}
                            onChange={(e) => field.handleChange(e.target.value)}
                          />
                          {field.state.meta.errors.length > 0 && (
                            <FieldDescription className="text-destructive">
                              {String(field.state.meta.errors[0])}
                            </FieldDescription>
                          )}
                        </Field>
                      )}
                    </loginForm.Field>
                    <loginForm.Field
                      name="password"
                      validators={{
                        onBlur: zodFieldValidator(loginFormSchema.shape.password),
                        onSubmit: zodFieldValidator(loginFormSchema.shape.password)
                      }}
                    >
                      {(field) => (
                        <Field>
                          <div className="flex items-center">
                            <FieldLabel htmlFor={field.name}>Password</FieldLabel>
                            <Link
                              to="/user/password-reset"
                              className="ml-auto inline-block text-sm underline-offset-4 hover:underline"
                            >
                              Forgot your password?
                            </Link>
                          </div>
                          <Input
                            id={field.name}
                            name={field.name}
                            type="password"
                            placeholder="Enter your password"
                            value={field.state.value}
                            onBlur={field.handleBlur}
                            onChange={(e) => field.handleChange(e.target.value)}
                          />
                          {field.state.meta.errors.length > 0 && (
                            <FieldDescription className="text-destructive">
                              {String(field.state.meta.errors[0])}
                            </FieldDescription>
                          )}
                        </Field>
                      )}
                    </loginForm.Field>
                  </FieldGroup>
                  <div className="pt-4">
                    <loginForm.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting]}>
                      {([canSubmit, isSubmitting]) => (
                        <Field>
                          <Button type="submit" disabled={!canSubmit || isSubmitting}>
                            {isSubmitting ? 'Signing in...' : 'Login'}
                          </Button>
                          <FieldDescription className="text-center">
                            Don&apos;t have an account? Contact an admin in your organisation.
                          </FieldDescription>
                        </Field>
                      )}
                    </loginForm.Subscribe>
                  </div>
                </form>
              ) : (
                <form
                  onSubmit={(e) => {
                    e.preventDefault()
                    mfaForm.handleSubmit()
                  }}
                >
                  <FieldGroup>
                    <mfaForm.Field name="mfaCode">
                      {(field) => (
                        <Field>
                          <FieldLabel htmlFor={field.name}>Verification Code</FieldLabel>
                          <Input
                            id={field.name}
                            name={field.name}
                            type="text"
                            placeholder="Enter 6-digit code"
                            maxLength={6}
                            value={field.state.value}
                            onBlur={field.handleBlur}
                            onChange={(e) => field.handleChange(e.target.value)}
                          />
                          {field.state.meta.errors.length > 0 && (
                            <FieldDescription className="text-destructive">
                              {String(field.state.meta.errors[0])}
                            </FieldDescription>
                          )}
                        </Field>
                      )}
                    </mfaForm.Field>
                  </FieldGroup>
                  <div className="pt-4">
                    <mfaForm.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting]}>
                      {([canSubmit, isSubmitting]) => (
                        <Field>
                          <Button type="submit" disabled={!canSubmit || isSubmitting}>
                            {isSubmitting ? 'Verifying...' : 'Verify'}
                          </Button>
                          <FieldDescription className="text-center">
                            <button
                              type="button"
                              disabled={resendCooldown > 0 || isSubmitting}
                              onClick={handleMfaResend}
                              className="cursor-pointer underline underline-offset-4 hover:no-underline disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              {resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : 'Resend code'}
                            </button>
                          </FieldDescription>
                        </Field>
                      )}
                    </mfaForm.Subscribe>
                  </div>
                </form>
              )}

              {error && <div>{error}</div>}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
